import { roleAtLeast } from '@atlas/types'
import { Button, Logo } from '@atlas/ui-primitives'
import { Plus, Search } from 'lucide-react'
import { useEffect, useState } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { breadcrumbFor, NAV_GROUPS, type NavModule } from '@/config/nav'
import { useAuth } from '@/hooks/useAuth'
import { useCan } from '@/lib/capabilities'
import { exitSelecting } from '@/lib/library-selection-store'
import { StatusBarHost } from '@/notifications/StatusBarHost'
import { BuildInfoBar } from './BuildInfoBar'
import { NavBadgeCount } from './NavBadgeCount'
import { SidebarUserFooter } from './SidebarUserFooter'

// The rail is dark, states straight from Atlas ToBe's `.nav-item`: base
// white/62 on transparent, hover white/6 fill + white/90, selected white/7
// fill + full white at medium weight — plus the gradient bar on the left
// (`[data-on]::before` in the mockup, a real element here).
const navLinkClass = ({ isActive }: { isActive: boolean }): string =>
	// The ring is inset: the rail is overflow-hidden, so an outset ring on a
	// full-width link is clipped at its edge and the focus becomes invisible.
	`relative flex w-full items-center gap-2.75 rounded-ds-md px-3 py-2.25 text-ds-sm transition-colors duration-120 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ds-ring ${
		isActive
			? 'bg-ds-background/7 font-medium text-ds-background'
			: 'text-ds-background/62 hover:bg-ds-background/6 hover:text-ds-background/90'
	}`

const childLinkClass = ({ isActive }: { isActive: boolean }): string =>
	`relative flex items-center gap-2.5 rounded-ds-md py-1.5 pl-10 pr-3 text-ds-sm transition-colors duration-120 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ds-ring ${
		isActive
			? 'bg-ds-background/7 font-medium text-ds-background'
			: 'text-ds-background/62 hover:bg-ds-background/6 hover:text-ds-background/90'
	}`

/** The selected item's marker: a 2px red→purple bar hugging the rail's left edge. */
function ActiveBar() {
	return (
		<span
			aria-hidden
			className="absolute bottom-[7px] left-0 top-[7px] w-0.5 rounded-r-ds-sm bg-gradient-to-b from-ds-danger to-ds-info"
		/>
	)
}

/**
 * Renders one nav entry and, when it owns the current section, its children.
 *
 * Every link is `end`-matched. Without it React Router treats a nav path as a
 * prefix, so `/admin` stays highlighted while `/admin/users` is open and two
 * entries look active at once. Parent highlighting is expressed by revealing its
 * children instead, which says "you are in this section" without claiming the
 * parent page itself is open.
 */
/** The tiny mono phase pill on placeholder / phase-2 entries — the mockup sidebar's `PH` / `P2` tags. */
/**
 * Not a `Badge`: this sits on the dark rail and tints with the background's own
 * alpha, which no Badge variant expresses. Sidebar chrome rather than content.
 */
function PhaseTag({ phase }: { phase: NavModule['phase'] }) {
	if (phase === 'PoC') return null
	return (
		<span className="rounded-ds-sm bg-ds-background/10 px-1 py-0.5 font-mono text-ds-4xs tracking-wider text-ds-background/40">
			{phase}
		</span>
	)
}

/** Fades with the rail: rendered at its final position always, revealed by the widening mask rather than re-laid-out. */
const maskedClass = (rail: boolean): string => `transition-opacity duration-200 ${rail ? 'opacity-0' : 'opacity-100'}`

function NavEntry({
	item,
	pathname,
	role,
	rail,
}: {
	item: NavModule
	pathname: string
	role: string
	/**
	 * Icon-only rail (not hover-expanded). The row's LAYOUT is identical in
	 * both states — the icon never moves; labels and tags sit at their
	 * expanded positions and are masked by the rail's clipped width, fading
	 * as it opens.
	 */
	rail: boolean
}) {
	const Icon = item.icon
	const children = (item.children ?? []).filter((child) => !child.minimumRole || roleAtLeast(role, child.minimumRole))
	const inSection = pathname === item.to || pathname.startsWith(`${item.to}/`)
	// A child that links to the parent's own path is the one lit there, so the two never look open at once.
	const childOwnsPath = children.some((child) => child.to === item.to)

	return (
		<div>
			<NavLink
				to={item.to}
				className={({ isActive }) => navLinkClass({ isActive: isActive && !childOwnsPath })}
				aria-current={childOwnsPath ? false : 'page'}
				end
				title={rail ? item.label : undefined}
			>
				{({ isActive }) => (
					<>
						{isActive && !childOwnsPath ? <ActiveBar /> : null}
						<Icon className="h-4 w-4 shrink-0 opacity-80" strokeWidth={1.75} />
						<span className={`flex-1 whitespace-nowrap ${maskedClass(rail)}`}>{item.label}</span>
						<span className={maskedClass(rail)}>
							<PhaseTag phase={item.phase} />
						</span>
					</>
				)}
			</NavLink>
			{inSection && children.length > 0 ? (
				<div className={`mt-0.5 flex flex-col gap-0.5 ${maskedClass(rail)}`}>
					{children.map((child) => {
						const ChildIcon = child.icon
						return (
							<NavLink key={child.to} to={child.to} className={childLinkClass} end>
								<ChildIcon className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} />
								<span className="whitespace-nowrap">{child.label}</span>
								{child.badge ? <NavBadgeCount badge={child.badge} /> : null}
							</NavLink>
						)
					})}
				</div>
			) : null}
		</div>
	)
}

function Sidebar() {
	const { user } = useAuth()
	const { pathname } = useLocation()
	const [hovered, setHovered] = useState(false)
	// An open user menu pins the rail open. Its content is portaled outside the
	// aside, so the pointer moving onto an item fires `onMouseLeave` here —
	// collapsing would unmount the menu before the item could be clicked.
	const [userMenuOpen, setUserMenuOpen] = useState(false)
	// The rail hides automatically: icon-only at rest, expanding while the
	// pointer is over it and settling back the moment it leaves.
	const rail = !hovered && !userMenuOpen

	return (
		<aside
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
			onFocus={() => setHovered(true)}
			onBlur={(event) => {
				// React's onBlur bubbles like `focusout`, so this fires as focus moves
				// between nav links too — only collapse once it leaves the rail
				// entirely, mirroring `:focus-within` for keyboard navigation.
				if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
					setHovered(false)
				}
			}}
			className={`relative z-50 flex flex-col overflow-hidden bg-ds-primary transition-[width] duration-200 ${rail ? 'w-15.5' : 'w-49'}`}
		>
			{/* Wordmark — the mark sits at the rail's center X and NEVER moves;
			    the caption lives at its expanded position and is masked in. */}
			<div className="w-49 flex-none px-5 pb-5.5 pt-7 text-ds-background">
				<Logo className="h-6.5 w-6.5" />
				<p
					className={`mt-3.5 whitespace-nowrap font-mono text-ds-3xs uppercase tracking-ds-wider text-ds-background/40 ${maskedClass(rail)}`}
				>
					Keenfolks X_ · Atlas
				</p>
			</div>

			{/* Grouped nav — fixed at the expanded width so rows never re-lay-out;
			    the aside's clipped width is the mask. Group headings keep their
			    height in both states, so nothing shifts vertically either. */}
			{/* `flex-1` (not `flex-none`): the nav absorbs the free space so the user
			    footer and build-info strip sit at the BOTTOM of the aside, where the
			    strip lines up with the status bar to form one continuous band. */}
			<nav className="w-49 flex-1 overflow-y-auto overflow-x-hidden px-3 py-1.5 [scrollbar-width:none]">
				{NAV_GROUPS.map((group) => {
					const items = group.items.filter(
						(item) => !item.minimumRole || roleAtLeast(user?.role ?? '', item.minimumRole),
					)
					if (items.length === 0) return null
					return (
						<div key={group.label} className="mb-1.5">
							<p
								className={`whitespace-nowrap px-3 pb-1.75 pt-5 font-mono text-ds-4xs uppercase tracking-ds-wider text-ds-background/26 ${maskedClass(rail)}`}
							>
								{group.label}
							</p>
							{items.map((item) => (
								<NavEntry key={item.to} item={item} pathname={pathname} role={user?.role ?? ''} rail={rail} />
							))}
						</div>
					)
				})}
			</nav>

			<SidebarUserFooter rail={rail} onMenuOpenChange={setUserMenuOpen} />

			{/* Same height and top border as the activity status bar, so the two line
			    up into one band across the bottom of the shell. */}
			<BuildInfoBar />
		</aside>
	)
}

// The topbar is black and continuous with the sidebar (Atlas ToBe), so the
// crumb reads in white alphas: mono, uppercase, letterspaced.
const crumbLinkClass =
	'rounded-ds-sm text-ds-background/40 hover:text-ds-background hover:underline focus-visible:ring-2 focus-visible:ring-ds-ring'

function Breadcrumb() {
	const { pathname } = useLocation()
	const trail = breadcrumbFor(pathname)

	return (
		// A real `nav` landmark with the conventional label, so it is reachable by
		// landmark navigation rather than being an anonymous row of text.
		<nav
			aria-label="Breadcrumb"
			className="flex items-center gap-2 whitespace-nowrap font-mono text-ds-3xs uppercase tracking-ds-wider text-ds-background/40"
		>
			{/* Always a link: on the home page it is the current page and marked as
			    such, but it stays clickable rather than becoming dead text. */}
			<NavLink to="/" className={crumbLinkClass} end>
				Atlas
			</NavLink>
			{trail.map((step) => (
				<span key={step.label} className="flex items-center gap-2">
					<span aria-hidden className="text-ds-background/25">
						/
					</span>
					{step.to ? (
						<NavLink to={step.to} className={crumbLinkClass} end>
							{step.label}
						</NavLink>
					) : (
						// The leaf is the page you are on: text, not a link to itself.
						<span aria-current="page" className="text-ds-background/80">
							{step.label}
						</span>
					)}
				</span>
			))}
		</nav>
	)
}

function Topbar() {
	const navigate = useNavigate()
	// Hidden rather than disabled: a read-only role has nothing to do with a
	// create action, and the button sits on every page. A disabled control here
	// would explain a restriction on screens that have nothing to do with it.
	const { canWork } = useCan()
	return (
		<header className="flex h-16 shrink-0 items-center gap-5 bg-ds-primary px-6">
			<Breadcrumb />
			<div className="relative mx-auto w-full max-w-115">
				<Search className="absolute left-3.25 top-1/2 h-4 w-4 -translate-y-1/2 text-ds-background/35" />
				<input
					type="search"
					placeholder="Search assets, orders, rules…"
					className="h-9.5 w-full rounded-ds-md border border-ds-background/14 bg-ds-background/6 pl-9 pr-4 text-ds-sm text-ds-background transition-colors duration-120 placeholder:text-ds-background/35 focus-visible:border-ds-background/60 focus-visible:bg-ds-background/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ds-ring"
				/>
			</div>
			{canWork ? (
				<Button
					onPress={() => void navigate('/work-orders/new')}
					className="flex h-9.5 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-ds-md border border-ds-background bg-ds-background px-4.5 text-ds-sm font-medium text-ds-text transition-all duration-120 hover:-translate-y-px hover:bg-ds-background hover:ring-2 hover:ring-ds-background/35"
				>
					<Plus className="h-4 w-4" />
					New work order
				</Button>
			) : null}
		</header>
	)
}

/**
 * Routes that render full-bleed (no centered reading column) because they manage
 * their own width — e.g. the board, which fills the viewport and scrolls its
 * columns horizontally. Everything else gets the `max-w-6xl` reading column.
 */
const FULL_BLEED_ROUTES = ['/board']

/**
 * The work-order WORKSPACE (`/work-orders/:id`, its edit view included) hides
 * the topbar: the tabbed surface owns the full column and carries its own
 * breadcrumb and actions. The creation form (`/work-orders/new`) and the
 * factory briefs list (`/work-orders/briefs`) are normal pages and keep it.
 */
const isWorkspaceRoute = (pathname: string): boolean => /^\/work-orders\/(?!(?:new|briefs)\/?$)[^/]+/.test(pathname)

/** Screens migrated to the UI Kit (ADR-0044), whose Figma lays them on the kit's grey background. */
const UI_KIT_ROUTES = ['/work-orders', '/work-orders/briefs']

/**
 * The Files experience: the Library grid and any asset opened from it.
 *
 * The ZIP selection is scoped to this whole region, not to the Library page —
 * `/assets` and `/assets/:id` are sibling routes, so the grid unmounts whenever a
 * card is opened, and clearing on that unmount would throw the selection away
 * during a normal check-before-downloading. This route-level test is the only
 * place in the tree that can tell "opened one of the selected files" apart from
 * "left Files".
 */
const isFilesRoute = (pathname: string): boolean => /^\/assets(\/.*)?$/.test(pathname)

export function Layout() {
	const { pathname } = useLocation()
	const hideTopbar = isWorkspaceRoute(pathname)

	useEffect(() => {
		if (!isFilesRoute(pathname)) exitSelecting()
	}, [pathname])
	// The workspace is full-bleed for the same reason the board is: it manages its
	// own width, and the reading column would squeeze a working surface (delivery
	// slots, briefing, side rail) that the user needs room in.
	const fullBleed = FULL_BLEED_ROUTES.includes(pathname) || hideTopbar
	return (
		<div className="flex h-screen overflow-hidden bg-ds-primary">
			<Sidebar />
			<div className="flex min-w-0 flex-1 flex-col">
				{hideTopbar ? null : <Topbar />}
				{/* The white sheet: the light canvas floating on the black shell (Atlas ToBe). */}
				<main
					className={`flex flex-1 flex-col overflow-y-auto [scrollbar-gutter:stable] rounded-tl-ds-xl px-8 py-8 ${UI_KIT_ROUTES.includes(pathname.replace(/\/$/, '')) ? 'bg-atlas-background' : 'bg-ds-surface-elevated'} ${hideTopbar ? 'mt-2' : ''}`}
				>
					{fullBleed ? <Outlet /> : <div className="mx-auto w-full max-w-6xl">{<Outlet />}</div>}
				</main>
				{/* Last in the column and outside <main>, so it occupies layout space
				    rather than overlaying the final row of page content, and survives
				    navigation instead of scrolling away with the Outlet. */}
				<StatusBarHost />
			</div>
		</div>
	)
}
